import { useState } from 'react'
import type { ToteEngagementMode, ToteStatusCoreData } from '@mote/api'
import { Block } from '@mote/ui'
import {
  IKnowWhatToDoButton,
  IMayNeedHelpButton,
  Root
} from './ToteCardComponent.styles'
import type { ToteCardSize } from './ToteCardProvider'
import { ToteCardProvider, useToteCardContext } from './ToteCardProvider'
import { ACTION_DATA, SCREEN_MODES, formatLastSeenDate } from './helpers'
import type {
  ToteCardMode,
  ToteData,
  ToteDataReturn,
  ToteEngagementModeWithAwaiting
} from './types'
import { Header, StudentTesponse, TeacherTesponse, Teply } from './views'

interface OnReplyObject {
  seenMeme: string
  mode: ToteEngagementMode
  teply: string | null
}

interface ToteCardComponentRootProps {
  toteStatus: ToteStatusCoreData
  onReply: (obj: OnReplyObject) => void
  children?: JSX.Element
  transparent?: boolean
  spacingTop?: boolean
  showTitle?: boolean
  getImageForMode: (mode: ToteEngagementMode) => string
}

interface ModeState {
  mode: ToteCardMode
  currentFlowMode: ToteEngagementMode | null
  responseText: string | null
  currentImage: string
}

function ToteCardComponentRoot(props: ToteCardComponentRootProps): JSX.Element {
  const toteCardContext = useToteCardContext()
  const [screenState, setScreenState] = useState<ModeState>({
    mode: getInitialMode(),
    currentFlowMode: props.toteStatus.lastLoopMode,
    responseText: props.toteStatus.lastTeply,
    currentImage: ''
  })
  const title =
    props.showTitle !== false
      ? `Respond to ${props.toteStatus.createdByName} 👇`
      : ''

  let content
  switch (screenState.mode) {
    case SCREEN_MODES.RESPONSE:
    case SCREEN_MODES.AWAITING: {
      const data = getData(screenState.mode)
      if (props.toteStatus.wasCreatedBySameUser) {
        content = (
          <TeacherTesponse
            mode={data.mode as ToteEngagementModeWithAwaiting}
            transparent={props.transparent}
            tesponse={screenState.responseText}
            studentName={props.toteStatus.studentName || 'Student'}
            image={screenState.currentImage}
            shouldShowInstructionLink={!props.toteStatus.lastLoopMode}
            lastSeenDate={formatLastSeenDate(props.toteStatus.lastSeenEpoch)}
          />
        )
      } else {
        content = (
          <StudentTesponse
            mode={data.mode as ToteEngagementMode}
            tesponse={screenState.responseText}
            image={screenState.currentImage}
            onChangeResponse={() => {
              setScreenState({
                mode: SCREEN_MODES.INITIAL,
                responseText: '',
                currentFlowMode: null,
                currentImage: ''
              })
            }}
          />
        )
      }
      break
    }
    case SCREEN_MODES.TEPLY: {
      const data = getData(screenState.mode)
      content = (
        <Teply
          data={data}
          image={screenState.currentImage}
          mode={screenState.currentFlowMode as ToteEngagementMode}
        />
      )
      break
    }
    default: {
      content = (
        <Block gap={toteCardContext.config.spacing}>
          {title && (
            <Header {...toteCardContext.config.textStyles}>{title}</Header>
          )}
          <Block layout={'vertical'} gap={8} spreadX>
            <IKnowWhatToDoButton
              //@ts-expect-error
              size={toteCardContext.config.size}
              aria-label={ACTION_DATA.affirmative.text}
              text={ACTION_DATA.affirmative.text}
              onClick={() => handleModeChange('affirmative')}
            />
            <IMayNeedHelpButton
              //@ts-expect-error
              size={toteCardContext.config.size}
              aria-label={ACTION_DATA.assistive.text}
              text={ACTION_DATA.assistive.text}
              onClick={() => handleModeChange('assistive')}
            />
          </Block>
        </Block>
      )
    }
  }

  const children = getChildren()
  return (
    <Root spacingTop={props.spacingTop} transparent={props.transparent}>
      {children}
      {content}
    </Root>
  )

  function getInitialMode() {
    if (props.toteStatus.lastLoopMode) {
      return SCREEN_MODES.RESPONSE
    }

    if (props.toteStatus.wasCreatedBySameUser) {
      return SCREEN_MODES.AWAITING
    }

    return SCREEN_MODES.INITIAL
  }

  function handleModeChange(mode: ToteEngagementMode) {
    const currentImage = props.getImageForMode(mode)
    setScreenState({
      ...screenState,
      mode: SCREEN_MODES.TEPLY,
      currentFlowMode: mode,
      currentImage
    })
    props.onReply({
      mode,
      teply: screenState.responseText,
      seenMeme: currentImage
    })
  }

  function getData<T extends keyof ToteData>(mode: T): ToteDataReturn<T> {
    const MODE_DATA: ToteData = {
      [SCREEN_MODES.TEPLY]: {
        title,
        placeholder: `Let ${props.toteStatus.createdByName} know what you plan to do`,
        mode: screenState.currentFlowMode,
        onSkip: () => {
          const currentImage = props.getImageForMode(
            screenState.currentFlowMode as ToteEngagementMode
          )
          setScreenState(s => ({
            ...s,
            mode: SCREEN_MODES.RESPONSE,
            currentImage
          }))
          props.onReply({
            seenMeme: currentImage,
            mode: screenState.currentFlowMode as ToteEngagementMode,
            teply: ''
          })
        },
        onSubmit: response => {
          const currentImage = props.getImageForMode(
            screenState.currentFlowMode as ToteEngagementMode
          )
          setScreenState(s => ({
            ...s,
            mode: SCREEN_MODES.RESPONSE,
            responseText: response,
            currentImage
          }))
          props.onReply({
            seenMeme: currentImage,
            mode: screenState.currentFlowMode as ToteEngagementMode,
            teply: response
          })
        }
      },
      [SCREEN_MODES.RESPONSE]: {
        mode: screenState.currentFlowMode
      },
      [SCREEN_MODES.AWAITING]: {
        mode: SCREEN_MODES.AWAITING
      }
    }
    return MODE_DATA[mode]
  }

  function getChildren() {
    if (
      [SCREEN_MODES.INITIAL, SCREEN_MODES.AWAITING].includes(screenState.mode)
    ) {
      return props.children || null
    }
    return null
  }
}

export interface ToteCardComponentProps extends ToteCardComponentRootProps {
  size?: ToteCardSize
}

export function ToteCardComponent(props: ToteCardComponentProps): JSX.Element {
  return (
    <ToteCardProvider size={props.size}>
      <ToteCardComponentRoot {...props} />
    </ToteCardProvider>
  )
}
